import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField, FormSection, Panel } from "@/components/FormField";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { JOB_STATUSES, PAYMENT_STATUSES, ui } from "@/lib/ui";

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const emptyForm = (driver = "") => ({
  date_time: nowLocal(), driver_name: driver, customer_name: "", vehicle_info: "", pickup_location: "",
  dropoff_location: "", reason_for_tow: "", towing_fee: "", payment_status: "Pending", status: "Completed",
});

const ChoiceSelect = ({ id, value, options, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger id={id} className={`h-11 ${ui.input}`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className={ui.menu}>
      {options.map((s) => <SelectItem key={s} value={s} data-testid={`${testId}-option-${s.toLowerCase()}`}>{s}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const JobEntryForm = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(() => emptyForm(user?.name !== "Admin" ? user?.name : ""));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/jobs", { ...form, date_time: new Date(form.date_time).toISOString(), towing_fee: Number(form.towing_fee) });
      toast.success("Job saved", { description: `${form.customer_name} · ${form.vehicle_info}` });
      setForm(emptyForm(form.driver_name));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const text = (k, label, props = {}) => (
    <FormField id={k} label={label}>
      <Input id={k} required value={form[k]} onChange={set(k)} className={`h-11 ${ui.input}`} data-testid={`job-${k.replace(/_/g, "-")}-input`} {...props} />
    </FormField>
  );

  return (
    <Panel className="p-5 sm:p-8">
      <form onSubmit={submit} data-testid="job-entry-form-container" className="space-y-8">
        <FormSection step="01" title="Job & driver">
          <div className="grid sm:grid-cols-2 gap-4">
            {text("date_time", "Date & time", { type: "datetime-local" })}
            {text("driver_name", "Driver name")}
          </div>
          {text("customer_name", "Customer name")}
        </FormSection>
        <FormSection step="02" title="Vehicle">
          {text("vehicle_info", "Make / Model / License plate", { placeholder: "Ford F-150 · 7ABC123" })}
        </FormSection>
        <FormSection step="03" title="Route">
          <div className="grid sm:grid-cols-2 gap-4">
            {text("pickup_location", "Pickup location")}
            {text("dropoff_location", "Drop-off location")}
          </div>
          <FormField id="reason_for_tow" label="Reason for tow">
            <Textarea id="reason_for_tow" required rows={2} value={form.reason_for_tow} onChange={set("reason_for_tow")} className={ui.input} data-testid="job-reason-input" />
          </FormField>
        </FormSection>
        <FormSection step="04" title="Payment & status">
          <div className="grid sm:grid-cols-3 gap-4">
            {text("towing_fee", "Fee (USD)", { type: "number", min: "0", step: "0.01", inputMode: "decimal", placeholder: "0.00" })}
            <FormField id="payment_status" label="Payment">
              <ChoiceSelect id="payment_status" value={form.payment_status} options={PAYMENT_STATUSES} onChange={setVal("payment_status")} testId="job-payment-status-select" />
            </FormField>
            <FormField id="status" label="Job status">
              <ChoiceSelect id="status" value={form.status} options={JOB_STATUSES} onChange={setVal("status")} testId="job-status-select" />
            </FormField>
          </div>
        </FormSection>
        <Button type="submit" disabled={busy} className={`${ui.btnPrimary} w-full h-12`} data-testid="job-entry-submit-button">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save job
        </Button>
      </form>
    </Panel>
  );
};
